import * as Types from '../actions/types';

export function reducer(state, action) {
  let next = state;
  if (action.type === Types.ACTION_LOOP) {
    const hasRange = action.start !== null && action.start !== undefined
      && action.end !== null && action.end !== undefined;
    next = { ...next, loop: hasRange ? { startTime: action.start, duration: action.end - action.start } : null };
  }

  // a whole-route loop starts at the first video frame
  const { currentRoute, loop, zoom } = next;
  if (currentRoute?.videoStartOffset && loop && zoom && loop.startTime === zoom.start && zoom.start === 0
    && currentRoute.videoStartOffset > loop.startTime) {
    next = {
      ...next,
      loop: {
        startTime: currentRoute.videoStartOffset,
        duration: loop.duration - currentRoute.videoStartOffset,
      },
    };
  }
  return next;
}

export function selectLoop(start, end) {
  return {
    type: Types.ACTION_LOOP,
    start,
    end,
  };
}

/**
 * @typedef {object} AttachedPlayer
 * @property {HTMLVideoElement} video
 * @property {(videoTimeMs: number) => void} seekTo  sets currentTime, resuming loading after a fatal error
 * @property {() => void} play  video.play(); a blocked play() leaves the element paused
 */

/** @type {AttachedPlayer | null} */
let attached = null;

export function attachPlayer(player) {
  attached = player;
  return () => {
    if (attached === player) attached = null;
  };
}

// route milliseconds; the loop ends no later than the video does
export function playbackBounds(state, video) {
  const videoStart = state.currentRoute?.videoStartOffset || 0;
  const videoDuration = video && Number.isFinite(video.duration) ? video.duration * 1000 : Infinity;
  const loopStart = state.loop?.startTime ?? state.zoom?.start ?? 0;
  const rangeEnd = state.loop ? state.loop.startTime + state.loop.duration : (state.zoom?.end ?? Infinity);
  return { videoStart, videoDuration, loopStart, loopEnd: Math.min(rangeEnd, videoStart + videoDuration) };
}

export function offsetOf(state) {
  const bounds = playbackBounds(state, attached?.video);
  if (!attached) {
    return bounds.loopStart;
  }
  return attached.video.currentTime * 1000 + bounds.videoStart;
}

// analytics listens for these actions

export function seek(offset) {
  return (dispatch, getState) => {
    if (attached) {
      attached.seekTo(seekTarget(offset, playbackBounds(getState(), attached.video)));
    }
    dispatch({ type: Types.ACTION_SEEK, offset: offsetOf(getState()), speed: attached?.video.playbackRate });
  };
}

// a new loop moves the playhead to its start only when the playhead is outside
// it; not a user seek, so no analytics event
export function keepInRange() {
  return (_dispatch, getState) => {
    if (!attached) {
      return;
    }
    const bounds = playbackBounds(getState(), attached.video);
    const start = playStart({ currentTime: attached.video.currentTime * 1000 }, bounds);
    if (start !== null) {
      attached.seekTo(start);
    }
  };
}

export function play() {
  return (dispatch, getState) => {
    attached?.play();
    dispatch({ type: Types.ACTION_PLAY, offset: offsetOf(getState()), speed: attached?.video.playbackRate });
  };
}

export function pause() {
  return (dispatch, getState) => {
    attached?.video.pause();
    dispatch({ type: Types.ACTION_PAUSE, offset: offsetOf(getState()), speed: attached?.video.playbackRate });
  };
}

/**
 * @typedef {object} VideoSnapshot
 * @property {number} currentTime
 * @property {boolean} paused
 * @property {boolean} seeking
 * @property {Array<[number, number]>} buffered  buffered ranges, start inclusive, end exclusive
 * @property {'' | 'missing' | 'network' | 'media'} err    fatal load error; buffered data still plays
 */

/**
 * @typedef {object} PlaybackBounds
 * @property {number} videoStart     route offset of video time 0 (videoStartOffset)
 * @property {number} videoDuration  video duration
 * @property {number} loopStart      loop or clip start, route offset
 * @property {number} loopEnd        loop or clip end, route offset
 */

const clamp = (x, lo, hi) => Math.min(Math.max(x, lo), hi);

export function bufferedAhead(el) {
  const range = el.buffered.find(([start, end]) => start <= el.currentTime && el.currentTime < end);
  return range ? range[1] - el.currentTime : 0;
}

export function canPlay(el) {
  return !el.seeking && bufferedAhead(el) > 0;
}

export function visibleError(el) {
  return el.err !== '' && !canPlay(el) ? el.err : '';
}

export function isLoading(el, bounds) {
  return el.err === '' && (el.seeking || (!el.paused && bufferedAhead(el) === 0 && el.currentTime < bounds.videoDuration));
}

export function isPlaying(el) {
  return !el.paused;
}

export function seekTarget(r, bounds) {
  return clamp(clamp(r, bounds.loopStart, bounds.loopEnd) - bounds.videoStart, 0, bounds.videoDuration);
}

// a new source starts at the loop start. Not seekTarget: on a route change the
// bounds still hold the old video's duration; the element clamps to the new one.
export function startPosition(bounds) {
  return Math.max(bounds.loopStart - bounds.videoStart, 0);
}

// timeupdate: playback stops at the end of the selected range, never past it;
// returns the video time to pause at, or null. At the video end the engine
// pauses on its own.
export function stopAt(el, bounds) {
  if (el.paused || el.seeking || el.currentTime >= bounds.videoDuration
    || el.currentTime + bounds.videoStart < bounds.loopEnd) {
    return null;
  }
  return clamp(bounds.loopEnd - bounds.videoStart, 0, el.currentTime);
}

// 'play' from outside the selected range (its end included) starts at the range start
export function playStart(el, bounds) {
  const offset = el.currentTime + bounds.videoStart;
  if (offset >= bounds.loopStart && offset < bounds.loopEnd) {
    return null;
  }
  return seekTarget(bounds.loopStart, bounds);
}

export function resumesOnline(el, online) {
  return online && el.err === 'network';
}
